import { useEffect, useState } from 'react';
import { validateCustomerDetails } from '../customerDetails';
import { getPublicErrorMessage } from '../publicError';
import { useAuth } from './AuthProvider';
import { useLanguage } from './LanguageProvider';

const EMPTY_SIGNUP = { name: '', email: '', phone: '', password: '' };

export function AuthModal() {
  const { authMode, closeAuth, login, openLogin, openSignup, signup } = useAuth();
  const { t } = useLanguage();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupValues, setSignupValues] = useState(EMPTY_SIGNUP);

  useEffect(() => {
    setError('');
    setSignupValues(EMPTY_SIGNUP);
  }, [authMode]);

  if (!authMode) return null;

  const signupValidation = validateCustomerDetails(signupValues);
  const canSignup = signupValidation.isValid && signupValues.password.length >= 6;

  const updateSignupValue = (field, value) => {
    setSignupValues((current) => ({ ...current, [field]: value }));
  };

  const signupField = (name, label, autoComplete, type) => {
    const fieldError = signupValidation.errors[name];
    const id = `auth${name[0].toUpperCase()}${name.slice(1)}`;

    return (
      <div className={`form-group${fieldError ? ' has-error' : ''}`}>
        <label htmlFor={id}>{label} <span className="required-mark" aria-hidden="true">*</span></label>
        <input
          aria-describedby={fieldError ? `${id}Error` : undefined}
          aria-invalid={Boolean(fieldError)}
          autoComplete={autoComplete}
          id={id}
          name={name}
          onChange={(event) => updateSignupValue(name, event.target.value)}
          required
          type={type}
          value={signupValues[name]}
        />
        {fieldError && <small className="field-error" id={`${id}Error`}>{fieldError}</small>}
      </div>
    );
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');

    const form = new FormData(event.currentTarget);
    if (authMode === 'signup' && !canSignup) return;

    setLoading(true);
    const result = authMode === 'login'
      ? await login(form.get('email').trim().toLowerCase(), form.get('password'))
      : await signup(
        signupValidation.normalized.name,
        signupValidation.normalized.email,
        signupValidation.normalized.phone,
        signupValues.password,
      );

    setLoading(false);
    if (result.error) {
      setError(getPublicErrorMessage(result.error, {
        accountExists: t('An account with this email already exists.', 'يوجد حساب مسجل بهذا البريد الإلكتروني.'),
        email: t('Please enter a valid email address.', 'يرجى إدخال بريد إلكتروني صحيح.'),
        invalidLogin: t('The email or password is incorrect.', 'البريد الإلكتروني أو كلمة المرور غير صحيحة.'),
        network: t('Please check your connection and try again.', 'يرجى التحقق من الاتصال والمحاولة مرة أخرى.'),
        phone: t(
          'Please enter a valid phone number with country code, e.g. +20109878708.',
          'يرجى إدخال رقم هاتف صحيح مع رمز الدولة، مثال: +20109878708.',
        ),
        rateLimit: t('Too many attempts. Please wait a moment and try again.', 'محاولات كثيرة. يرجى الانتظار قليلاً ثم المحاولة مرة أخرى.'),
      }, t('We could not complete your request. Please try again.', 'تعذر إكمال طلبك. يرجى المحاولة مرة أخرى.')));
    } else {
      closeAuth();
    }
  };

  return (
    <div className="auth-modal" onClick={(event) => event.target === event.currentTarget && closeAuth()} role="presentation">
      <div aria-labelledby="authModalTitle" aria-modal="true" className="auth-modal-content" role="dialog">
        <button className="auth-close" onClick={closeAuth} type="button" aria-label="Close">&times;</button>
        <div className="auth-form">
          <h2 id="authModalTitle">{authMode === 'login' ? t('Login', 'تسجيل الدخول') : t('Sign Up', 'إنشاء حساب')}</h2>
          <p className="auth-subtitle">
            {authMode === 'login'
              ? t('Sign in to view your past quotes', 'قم بتسجيل الدخول لعرض عروض الأسعار السابقة')
              : t('Create an account to save your quotes', 'أنشئ حسابًا لحفظ عروض الأسعار الخاصة بك')}
          </p>
          <form noValidate={authMode === 'signup'} onSubmit={submit}>
            {authMode === 'signup' && (
              <>
                {signupField('name', t('Full Name', 'الاسم الكامل'), 'name', 'text')}
                {signupField('phone', t('Phone Number', 'رقم الهاتف'), 'tel', 'tel')}
                {signupField('email', t('Email', 'البريد الإلكتروني'), 'email', 'email')}
                <div className="form-group">
                  <label htmlFor="authPassword">
                    {t('Password', 'كلمة المرور')} <span className="required-mark" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="authPassword"
                    minLength={6}
                    name="password"
                    onChange={(event) => updateSignupValue('password', event.target.value)}
                    required
                    type="password"
                    value={signupValues.password}
                  />
                </div>
              </>
            )}
            {authMode === 'login' && (
              <>
                <div className="form-group">
                  <label htmlFor="authEmail">{t('Email', 'البريد الإلكتروني')}</label>
                  <input id="authEmail" name="email" required type="email" />
                </div>
                <div className="form-group">
                  <label htmlFor="authPassword">{t('Password', 'كلمة المرور')}</label>
                  <input id="authPassword" minLength={6} name="password" required type="password" />
                </div>
              </>
            )}
            <button className="btn-auth-submit" disabled={loading || (authMode === 'signup' && !canSignup)} type="submit">
              {loading ? t('Loading...', 'جاري التحميل...') : authMode === 'login' ? t('Login', 'تسجيل الدخول') : t('Sign Up', 'إنشاء حساب')}
            </button>
          </form>
          <p className="auth-switch">
            {authMode === 'login' ? t("Don't have an account?", 'ليس لديك حساب؟') : t('Already have an account?', 'لديك حساب بالفعل؟')}{' '}
            <button onClick={authMode === 'login' ? openSignup : openLogin} type="button">
              {authMode === 'login' ? t('Sign up', 'سجل الآن') : t('Login', 'تسجيل الدخول')}
            </button>
          </p>
          {error && <div className="auth-error">{error}</div>}
        </div>
      </div>
    </div>
  );
}
